import styled, { createGlobalStyle } from "styled-components";

// Advanced Global Design System
const GlobalStyles = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&family=Poppins:wght@300;400;500;600;700;800;900&family=JetBrains+Mono:wght@400;500;600;700&display=swap');
  
  :root {
    /* Enhanced Color Palette */
    --primary-50: #eff6ff;
    --primary-100: #dbeafe;
    --primary-200: #bfdbfe;
    --primary-300: #93c5fd;
    --primary-400: #60a5fa;
    --primary-500: #3b82f6;
    --primary-600: #2563eb;
    --primary-700: #1d4ed8;
    --primary-800: #1e40af;
    --primary-900: #1e3a8a;
    --primary-950: #172554;
    
    --secondary-50: #f0fdf4;
    --secondary-100: #dcfce7;
    --secondary-200: #bbf7d0;
    --secondary-300: #86efac;
    --secondary-400: #4ade80;
    --secondary-500: #22c55e;
    --secondary-600: #16a34a;
    --secondary-700: #15803d;
    --secondary-800: #166534;
    --secondary-900: #14532d;
    
    --accent-50: #fef7ff;
    --accent-100: #fce7ff;
    --accent-200: #f8d4ff;
    --accent-300: #f0b3ff;
    --accent-400: #e580ff;
    --accent-500: #d946ef;
    --accent-600: #c026d3;
    --accent-700: #a21caf;
    --accent-800: #86198f;
    --accent-900: #701a75;
    
    /* Neutral Grays */
    --gray-0: #ffffff;
    --gray-25: #fcfcfd;
    --gray-50: #f9fafb;
    --gray-100: #f3f4f6;
    --gray-200: #e5e7eb;
    --gray-300: #d1d5db;
    --gray-400: #9ca3af;
    --gray-500: #6b7280;
    --gray-600: #4b5563;
    --gray-700: #374151;
    --gray-800: #1f2937;
    --gray-900: #111827;
    --gray-950: #030712;
    
    /* Status Colors */
    --success-50: #ecfdf5;
    --success-500: #10b981;
    --success-600: #059669;
    --warning-50: #fffbeb;
    --warning-500: #f59e0b;
    --warning-600: #d97706;
    --error-50: #fef2f2;
    --error-500: #ef4444;
    --error-600: #dc2626;
    --info-50: #eff6ff;
    --info-500: #3b82f6;
    --info-600: #2563eb;
    
    /* Enhanced Spacing Scale */
    --space-px: 1px;
    --space-0: 0;
    --space-0-5: 0.125rem;
    --space-1: 0.25rem;
    --space-1-5: 0.375rem;
    --space-2: 0.5rem;
    --space-2-5: 0.625rem;
    --space-3: 0.75rem;
    --space-3-5: 0.875rem;
    --space-4: 1rem;
    --space-5: 1.25rem;
    --space-6: 1.5rem;
    --space-7: 1.75rem;
    --space-8: 2rem;
    --space-9: 2.25rem;
    --space-10: 2.5rem;
    --space-11: 2.75rem;
    --space-12: 3rem;
    --space-14: 3.5rem;
    --space-16: 4rem;
    --space-20: 5rem;
    --space-24: 6rem;
    --space-28: 7rem;
    --space-32: 8rem;
    --space-36: 9rem;
    --space-40: 10rem;
    --space-44: 11rem;
    --space-48: 12rem;
    --space-52: 13rem;
    --space-56: 14rem;
    --space-60: 15rem;
    --space-64: 16rem;
    --space-72: 18rem;
    --space-80: 20rem;
    --space-96: 24rem;
    
    /* Responsive Breakpoints */
    --breakpoint-xs: 375px;
    --breakpoint-sm: 640px;
    --breakpoint-md: 768px;
    --breakpoint-lg: 1024px;
    --breakpoint-xl: 1280px;
    --breakpoint-2xl: 1536px;
    
    /* Typography Scale */
    --text-xs: 0.75rem;
    --text-sm: 0.875rem;
    --text-base: 1rem;
    --text-lg: 1.125rem;
    --text-xl: 1.25rem;
    --text-2xl: 1.5rem;
    --text-3xl: 1.875rem;
    --text-4xl: 2.25rem;
    --text-5xl: 3rem;
    --text-6xl: 3.75rem;
    --text-7xl: 4.5rem;
    --text-8xl: 6rem;
    --text-9xl: 8rem;
    
    /* Line Heights */
    --leading-none: 1;
    --leading-tight: 1.25;
    --leading-snug: 1.375;
    --leading-normal: 1.5;
    --leading-relaxed: 1.625;
    --leading-loose: 2;
    
    /* Border Radius */
    --radius-none: 0;
    --radius-sm: 0.125rem;
    --radius-DEFAULT: 0.25rem;
    --radius-md: 0.375rem;
    --radius-lg: 0.5rem;
    --radius-xl: 0.75rem;
    --radius-2xl: 1rem;
    --radius-3xl: 1.5rem;
    --radius-full: 9999px;
    
    /* Enhanced Shadows */
    --shadow-xs: 0 1px 2px 0 rgb(0 0 0 / 0.05);
    --shadow-sm: 0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1);
    --shadow-DEFAULT: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
    --shadow-md: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
    --shadow-lg: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
    --shadow-xl: 0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1);
    --shadow-2xl: 0 25px 50px -12px rgb(0 0 0 / 0.25);
    --shadow-inner: inset 0 2px 4px 0 rgb(0 0 0 / 0.05);
    
    /* Glassmorphism Effects */
    --glass-bg: rgba(255, 255, 255, 0.25);
    --glass-border: rgba(255, 255, 255, 0.18);
    --glass-backdrop: blur(20px);
    
    /* Gradient Definitions */
    --gradient-primary: linear-gradient(135deg, var(--primary-500) 0%, var(--primary-700) 100%);
    --gradient-secondary: linear-gradient(135deg, var(--secondary-500) 0%, var(--secondary-700) 100%);
    --gradient-accent: linear-gradient(135deg, var(--accent-500) 0%, var(--accent-700) 100%);
    --gradient-rainbow: linear-gradient(135deg, var(--primary-500) 0%, var(--accent-500) 50%, var(--secondary-500) 100%);
    --gradient-dark: linear-gradient(135deg, var(--gray-800) 0%, var(--gray-900) 100%);
    
    /* Transitions */
    --transition-fast: 150ms ease;
    --transition-normal: 300ms ease;
    --transition-slow: 500ms ease;
    
    /* Z-Index Scale */
    --z-0: 0;
    --z-10: 10;
    --z-20: 20;
    --z-30: 30;
    --z-40: 40;
    --z-50: 50;
    --z-auto: auto;
    
    /* Breakpoints */
    --bp-sm: 640px;
    --bp-md: 768px;
    --bp-lg: 1024px;
    --bp-xl: 1280px;
    --bp-2xl: 1536px;
  }

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }
  
  /* Mobile-first responsive design optimizations */
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }
  
  /* Improve touch interactions on mobile */
  button,
  [role="button"],
  input[type="submit"],
  input[type="reset"],
  input[type="button"] {
    -webkit-tap-highlight-color: transparent;
    touch-action: manipulation;
    min-height: 44px; /* Apple's recommended minimum touch target */
    min-width: 44px;
    
    @media (max-width: 768px) {
      -webkit-touch-callout: none;
      -webkit-user-select: none;
      user-select: none;
    }
  }
  
  /* Improve scrolling on mobile */
  html {
    -webkit-text-size-adjust: 100%;
    -ms-text-size-adjust: 100%;
    touch-action: manipulation;
  }
  
  /* Prevent zoom on inputs for iOS */
  @media (max-width: 768px) {
    input[type="text"],
    input[type="email"],
    input[type="password"],
    input[type="number"],
    input[type="search"],
    input[type="tel"],
    input[type="url"],
    select,
    textarea {
      font-size: 16px; /* Prevents zoom on iOS */
    }
  }
  
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  html {
    scroll-behavior: smooth;
    line-height: var(--leading-normal);
    -webkit-text-size-adjust: 100%;
    font-feature-settings: "cv02", "cv03", "cv04", "cv11";
    overflow-x: hidden;
  }

  body {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif;
    background: var(--gray-0);
    color: var(--gray-900);
    min-height: 100vh;
    font-size: var(--text-base);
    font-weight: 400;
    line-height: var(--leading-relaxed);
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-rendering: optimizeLegibility;
    font-feature-settings: "liga", "kern";
    overflow-x: hidden;
    
    /* Mobile optimizations */
    @media (max-width: 768px) {
      font-size: var(--text-sm);
      line-height: var(--leading-normal);
    }
    
    @media (max-width: 480px) {
      font-size: 14px;
    }
  }

  a {
    color: var(--primary-600);
    text-decoration: none;
    transition: all var(--transition-fast);
    
    &:hover {
      color: var(--primary-700);
    }
    
    &:focus {
      outline: 2px solid var(--primary-500);
      outline-offset: 2px;
      border-radius: var(--radius-sm);
    }
  }

  h1, h2, h3, h4, h5, h6 {
    font-family: 'Poppins', 'Inter', sans-serif;
    font-weight: 700;
    line-height: var(--leading-tight);
    color: var(--gray-900);
    margin-bottom: var(--space-4);
    letter-spacing: -0.025em;
  }

  h1 { 
    font-size: var(--text-6xl); 
    font-weight: 800; 
    line-height: var(--leading-none);
    letter-spacing: -0.05em;
    
    @media (max-width: 1024px) {
      font-size: var(--text-5xl);
    }
    
    @media (max-width: 768px) {
      font-size: var(--text-4xl);
    }
    
    @media (max-width: 480px) {
      font-size: var(--text-3xl);
    }
  }
  
  h2 { 
    font-size: var(--text-4xl); 
    font-weight: 700;
    
    @media (max-width: 1024px) {
      font-size: var(--text-3xl);
    }
    
    @media (max-width: 768px) {
      font-size: var(--text-2xl);
    }
    
    @media (max-width: 480px) {
      font-size: var(--text-xl);
    }
  }
  
  h3 { 
    font-size: var(--text-3xl); 
    font-weight: 600;
    
    @media (max-width: 768px) {
      font-size: var(--text-2xl);
    }
    
    @media (max-width: 480px) {
      font-size: var(--text-lg);
    }
  }
  
  h4 { 
    font-size: var(--text-2xl); 
    font-weight: 600;
    
    @media (max-width: 768px) {
      font-size: var(--text-xl);
    }
    
    @media (max-width: 480px) {
      font-size: var(--text-lg);
    }
  }
  
  h5 { 
    font-size: var(--text-xl); 
    font-weight: 600;
  }
  
  h6 { 
    font-size: var(--text-lg); 
    font-weight: 600;
  }

  p {
    margin-bottom: var(--space-4);
    line-height: var(--leading-relaxed);
    color: var(--gray-600);
    font-size: var(--text-base);
  }

  button {
    font-family: inherit;
    font-weight: 500;
    border: none;
    border-radius: var(--radius-lg);
    cursor: pointer;
    transition: all var(--transition-normal);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: var(--space-2);
    text-decoration: none;
    position: relative;
    overflow: hidden;
    
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
      transform: none !important;
    }
    
    &:focus {
      outline: 2px solid var(--primary-500);
      outline-offset: 2px;
    }
  }

  input, textarea, select {
    font-family: inherit;
    font-size: var(--text-base);
    border: 1px solid var(--gray-300);
    border-radius: var(--radius-lg);
    padding: var(--space-3) var(--space-4);
    transition: all var(--transition-fast);
    background: var(--gray-0);
    
    &:focus {
      outline: none;
      border-color: var(--primary-500);
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }
    
    &::placeholder {
      color: var(--gray-400);
    }
  }

  ::selection {
    background: var(--primary-100);
    color: var(--primary-900);
  }

  /* Enhanced Scrollbar */
  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  
  ::-webkit-scrollbar-track {
    background: var(--gray-100);
    border-radius: var(--radius-full);
  }
  
  ::-webkit-scrollbar-thumb {
    background: var(--gray-300);
    border-radius: var(--radius-full);
    
    &:hover {
      background: var(--gray-400);
    }
  }

  /* Animation Keyframes */
  @keyframes fadeIn {
    from { 
      opacity: 0; 
      transform: translateY(20px); 
    }
    to { 
      opacity: 1; 
      transform: translateY(0); 
    }
  }

  @keyframes slideInLeft {
    from { 
      opacity: 0;
      transform: translateX(-50px); 
    }
    to { 
      opacity: 1;
      transform: translateX(0); 
    }
  }

  @keyframes slideInRight {
    from { 
      opacity: 0;
      transform: translateX(50px); 
    }
    to { 
      opacity: 1;
      transform: translateX(0); 
    }
  }

  @keyframes scaleIn {
    from { 
      opacity: 0;
      transform: scale(0.9); 
    }
    to { 
      opacity: 1;
      transform: scale(1); 
    }
  }

  @keyframes float {
    0%, 100% { 
      transform: translateY(0px); 
    }
    50% { 
      transform: translateY(-10px); 
    }
  }

  @keyframes pulse {
    0%, 100% { 
      opacity: 1; 
    }
    50% { 
      opacity: 0.5; 
    }
  }

  @keyframes spin {
    from { 
      transform: rotate(0deg); 
    }
    to { 
      transform: rotate(360deg); 
    }
  }

  @keyframes bounce {
    0%, 20%, 53%, 80%, 100% {
      transform: translate3d(0,0,0);
    }
    40%, 43% {
      transform: translate3d(0, -30px, 0);
    }
    70% {
      transform: translate3d(0, -15px, 0);
    }
    90% {
      transform: translate3d(0, -4px, 0);
    }
  }

  /* Utility Animation Classes */
  .animate-fade-in {
    animation: fadeIn 0.6s ease-out;
  }

  .animate-slide-in-left {
    animation: slideInLeft 0.6s ease-out;
  }

  .animate-slide-in-right {
    animation: slideInRight 0.6s ease-out;
  }

  .animate-scale-in {
    animation: scaleIn 0.4s ease-out;
  }

  .animate-float {
    animation: float 3s ease-in-out infinite;
  }

  .animate-pulse {
    animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  }

  .animate-spin {
    animation: spin 1s linear infinite;
  }

  .animate-bounce {
    animation: bounce 1s infinite;
  }

  /* Utility Classes */
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  .glassmorphism {
    background: var(--glass-bg);
    backdrop-filter: var(--glass-backdrop);
    -webkit-backdrop-filter: var(--glass-backdrop);
    border: 1px solid var(--glass-border);
  }

  .gradient-text {
    background: var(--gradient-rainbow);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  /* Overflow Utility Classes */
  .overflow-hidden {
    overflow: hidden;
  }
  
  .overflow-x-hidden {
    overflow-x: hidden;
  }
  
  .overflow-y-hidden {
    overflow-y: hidden;
  }
  
  .overflow-auto {
    overflow: auto;
  }
  
  .overflow-x-auto {
    overflow-x: auto;
  }
  
  .overflow-y-auto {
    overflow-y: auto;
  }
`;

export default GlobalStyles;

// Enhanced Button Components with Modern Design
export const PrimaryButton = styled.button`
  background: linear-gradient(135deg, 
    #3b82f6 0%, 
    #8b5cf6 25%, 
    #ec4899 50%, 
    #f59e0b 75%, 
    #3b82f6 100%
  );
  background-size: 300% 300%;
  color: var(--gray-0);
  padding: var(--space-4) var(--space-8);
  font-size: var(--text-base);
  font-weight: 700;
  border-radius: var(--radius-2xl);
  box-shadow: 
    0 4px 20px rgba(59, 130, 246, 0.25),
    0 8px 32px rgba(147, 51, 234, 0.15),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
  position: relative;
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  min-height: 48px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  animation: gradient-shift 6s ease infinite;
  
  @keyframes gradient-shift {
    0%, 100% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
  }
  
  @media (max-width: 768px) {
    padding: var(--space-3) var(--space-6);
    font-size: var(--text-sm);
    min-height: 44px;
  }
  
  @media (max-width: 480px) {
    width: 100%;
    margin-bottom: var(--space-3);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
    transition: left 0.6s ease;
  }
  
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: var(--radius-2xl);
    padding: 2px;
    background: linear-gradient(135deg, 
      rgba(255, 255, 255, 0.2) 0%, 
      rgba(255, 255, 255, 0.1) 50%, 
      rgba(255, 255, 255, 0.2) 100%
    );
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    mask-composite: subtract;
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-3px) scale(1.05);
    box-shadow: 
      0 8px 32px rgba(59, 130, 246, 0.4),
      0 16px 64px rgba(147, 51, 234, 0.25),
      inset 0 1px 0 rgba(255, 255, 255, 0.3);
    animation-duration: 3s;
    
    &::before {
      left: 100%;
    }
    
    &::after {
      opacity: 1;
    }
    
    @media (max-width: 768px) {
      transform: translateY(-2px) scale(1.02);
    }
  }
  
  &:active {
    transform: translateY(-1px) scale(1.02);
    transition: all 0.1s ease;
  }
  
  &:focus {
    outline: 3px solid rgba(59, 130, 246, 0.5);
    outline-offset: 2px;
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    animation: none;
    
    &:hover {
      transform: none;
      box-shadow: 
        0 4px 20px rgba(59, 130, 246, 0.25),
        0 8px 32px rgba(147, 51, 234, 0.15);
    }
  }
`;

export const SecondaryButton = styled.button`
  background: var(--gray-0);
  color: var(--primary-600);
  border: 2px solid var(--primary-200);
  padding: var(--space-4) var(--space-8);
  font-size: var(--text-base);
  font-weight: 600;
  border-radius: var(--radius-xl);
  transition: all var(--transition-normal);
  position: relative;
  overflow: hidden;
  min-height: 48px; /* Better touch target for mobile */
  
  @media (max-width: 768px) {
    padding: var(--space-3) var(--space-6);
    font-size: var(--text-sm);
    min-height: 44px;
  }
  
  @media (max-width: 480px) {
    width: 100%;
    margin-bottom: var(--space-3);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 0;
    height: 100%;
    background: var(--primary-50);
    transition: width var(--transition-normal);
    z-index: -1;
  }
  
  &:hover {
    border-color: var(--primary-400);
    transform: translateY(-2px);
    box-shadow: var(--shadow-lg);
    
    &::before {
      width: 100%;
    }
    
    @media (max-width: 768px) {
      transform: translateY(-1px);
    }
  }
  
  &:focus {
    outline: 2px solid var(--primary-300);
    outline-offset: 2px;
  }
`;

export const OutlineButton = styled.button`
  background: transparent;
  color: var(--gray-700);
  border: 2px solid var(--gray-300);
  padding: var(--space-4) var(--space-8);
  font-size: var(--text-base);
  font-weight: 500;
  border-radius: var(--radius-xl);
  transition: all var(--transition-normal);
  
  &:hover {
    border-color: var(--gray-400);
    background: var(--gray-50);
    transform: translateY(-1px);
    box-shadow: var(--shadow-md);
  }
  
  &:focus {
    outline: 2px solid var(--gray-400);
    outline-offset: 2px;
  }
`;

export const GhostButton = styled.button`
  background: transparent;
  color: var(--gray-600);
  border: none;
  padding: var(--space-3) var(--space-6);
  font-size: var(--text-base);
  font-weight: 500;
  border-radius: var(--radius-lg);
  transition: all var(--transition-fast);
  
  &:hover {
    background: var(--gray-100);
    color: var(--gray-900);
  }
  
  &:focus {
    outline: 2px solid var(--gray-300);
    outline-offset: 2px;
  }
`;

export const IconButton = styled.button`
  background: var(--gray-100);
  color: var(--gray-600);
  border: none;
  padding: var(--space-3);
  border-radius: var(--radius-full);
  transition: all var(--transition-fast);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  
  &:hover {
    background: var(--gray-200);
    color: var(--gray-900);
    transform: scale(1.05);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  &:focus {
    outline: 2px solid var(--primary-300);
    outline-offset: 2px;
  }
`;

// Advanced Container Components
export const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--space-6);
  width: 100%;
  
  @media (max-width: 1024px) {
    padding: 0 var(--space-5);
    max-width: 960px;
  }
  
  @media (max-width: ${props => props.theme?.breakpoints?.md || '768px'}) {
    padding: 0 var(--space-4);
    max-width: 100%;
  }
  
  @media (max-width: 480px) {
    padding: 0 var(--space-3);
  }
  
  @media (max-width: 375px) {
    padding: 0 var(--space-2);
  }
  
  &.narrow {
    max-width: 800px;
    
    @media (max-width: 768px) {
      max-width: 100%;
    }
  }
  
  &.wide {
    max-width: 1400px;
    
    @media (max-width: 1024px) {
      max-width: 100%;
    }
  }
  
  &.full {
    max-width: none;
  }
`;

export const Section = styled.section`
  padding: var(--space-20) 0;
  position: relative;
  overflow-x: hidden;
  width: 100%;
  
  @media (max-width: 1024px) {
    padding: var(--space-16) 0;
  }
  
  @media (max-width: 768px) {
    padding: var(--space-12) 0;
  }
  
  @media (max-width: 480px) {
    padding: var(--space-10) 0;
  }
  
  &.compact {
    padding: var(--space-12) 0;
    
    @media (max-width: 1024px) {
      padding: var(--space-10) 0;
    }
    
    @media (max-width: 768px) {
      padding: var(--space-8) 0;
    }
    
    @media (max-width: 480px) {
      padding: var(--space-6) 0;
    }
  }
  
  &.spacious {
    padding: var(--space-32) 0;
    
    @media (max-width: 768px) {
      padding: var(--space-20) 0;
    }
  }
`;

export const Card = styled.div`
  background: var(--gray-0);
  border-radius: var(--radius-2xl);
  padding: var(--space-8);
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--gray-200);
  transition: all var(--transition-normal);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: var(--gradient-primary);
    transform: scaleX(0);
    transition: transform var(--transition-normal);
    transform-origin: left;
  }
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: var(--shadow-xl);
    border-color: var(--primary-200);
    
    &::before {
      transform: scaleX(1);
    }
  }
  
  &.interactive {
    cursor: pointer;
  }
  
  &.glass {
    background: var(--glass-bg);
    backdrop-filter: var(--glass-backdrop);
    -webkit-backdrop-filter: var(--glass-backdrop);
    border: 1px solid var(--glass-border);
  }
  
  &.minimal {
    border: none;
    box-shadow: var(--shadow-xs);
    
    &:hover {
      box-shadow: var(--shadow-md);
    }
  }
`;

export const GradientCard = styled(Card)`
  background: var(--gradient-primary);
  color: var(--gray-0);
  border: none;
  
  &::before {
    background: rgba(255, 255, 255, 0.3);
  }
  
  h1, h2, h3, h4, h5, h6 {
    color: var(--gray-0);
  }
  
  p {
    color: rgba(255, 255, 255, 0.9);
  }
`;

// Advanced Grid Systems
export const Grid = styled.div`
  display: grid;
  gap: var(--space-6);
  
  &.cols-1 {
    grid-template-columns: 1fr;
  }
  
  &.cols-2 {
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    
    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      gap: var(--space-4);
    }
  }
  
  &.cols-3 {
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    
    @media (max-width: 1024px) {
      grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    }
    
    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      gap: var(--space-4);
    }
  }
  
  &.cols-4 {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    
    @media (max-width: 1024px) {
      grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    }
    
    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      gap: var(--space-4);
    }
  }
  
  &.cols-5 {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    
    @media (max-width: 1024px) {
      grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    }
    
    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      gap: var(--space-4);
    }
  }
  
  &.cols-6 {
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    
    @media (max-width: 1024px) {
      grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    }
    
    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      gap: var(--space-4);
    }
  }
  
  @media (max-width: 768px) {
    gap: var(--space-4);
  }
  
  @media (max-width: 480px) {
    gap: var(--space-3);
  }
  
  &.tight {
    gap: var(--space-4);
    
    @media (max-width: 480px) {
      gap: var(--space-2);
    }
  }
  
  &.loose {
    gap: var(--space-10);
    
    @media (max-width: 768px) {
      gap: var(--space-6);
    }
    
    @media (max-width: 480px) {
      gap: var(--space-4);
    }
  }
`;

export const FlexContainer = styled.div`
  display: flex;
  gap: var(--space-4);
  
  &.center {
    align-items: center;
    justify-content: center;
  }
  
  &.between {
    justify-content: space-between;
    align-items: center;
  }
  
  &.around {
    justify-content: space-around;
    align-items: center;
  }
  
  &.evenly {
    justify-content: space-evenly;
    align-items: center;
  }
  
  &.start {
    align-items: flex-start;
  }
  
  &.end {
    align-items: flex-end;
  }
  
  &.column {
    flex-direction: column;
  }
  
  &.wrap {
    flex-wrap: wrap;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: var(--space-3);
    
    &.mobile-row {
      flex-direction: row;
    }
  }
`;

// Typography Components
export const Heading = styled.h1`
  font-family: 'Poppins', sans-serif;
  font-weight: ${props => props.weight || '700'};
  color: ${props => props.color || 'var(--gray-900)'};
  margin-bottom: var(--space-4);
  line-height: var(--leading-tight);
  letter-spacing: -0.025em;
  
  &.gradient {
    background: var(--gradient-rainbow);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  &.center {
    text-align: center;
  }
`;

export const Text = styled.p`
  color: ${props => props.color || 'var(--gray-600)'};
  font-size: ${props => props.size || 'var(--text-base)'};
  line-height: var(--leading-relaxed);
  margin-bottom: var(--space-4);
  
  &.large {
    font-size: var(--text-lg);
  }
  
  &.small {
    font-size: var(--text-sm);
  }
  
  &.center {
    text-align: center;
  }
  
  &.muted {
    color: var(--gray-500);
  }
`;

// Badge Component
export const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  padding: var(--space-1) var(--space-3);
  font-size: var(--text-xs);
  font-weight: 600;
  border-radius: var(--radius-full);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  
  &.primary {
    background: var(--primary-100);
    color: var(--primary-700);
  }
  
  &.secondary {
    background: var(--secondary-100);
    color: var(--secondary-700);
  }
  
  &.success {
    background: var(--success-50);
    color: var(--success-600);
  }
  
  &.warning {
    background: var(--warning-50);
    color: var(--warning-600);
  }
  
  &.error {
    background: var(--error-50);
    color: var(--error-600);
  }
  
  &.outline {
    background: transparent;
    border: 1px solid currentColor;
  }
`;

// Input Components
export const Input = styled.input`
  font-family: inherit;
  font-size: var(--text-base);
  border: 2px solid var(--gray-200);
  border-radius: var(--radius-xl);
  padding: var(--space-4);
  transition: all var(--transition-fast);
  background: var(--gray-0);
  width: 100%;
  
  &:focus {
    outline: none;
    border-color: var(--primary-500);
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
  
  &::placeholder {
    color: var(--gray-400);
  }
  
  &:disabled {
    background: var(--gray-50);
    color: var(--gray-500);
    cursor: not-allowed;
  }
`;

export const TextArea = styled.textarea`
  font-family: inherit;
  font-size: var(--text-base);
  border: 2px solid var(--gray-200);
  border-radius: var(--radius-xl);
  padding: var(--space-4);
  transition: all var(--transition-fast);
  background: var(--gray-0);
  width: 100%;
  min-height: 120px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: var(--primary-500);
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
  
  &::placeholder {
    color: var(--gray-400);
  }
  
  &:disabled {
    background: var(--gray-50);
    color: var(--gray-500);
    cursor: not-allowed;
  }
`;

// Loading Components
export const Spinner = styled.div`
  width: ${props => props.size || '24px'};
  height: ${props => props.size || '24px'};
  border: 2px solid var(--gray-200);
  border-top: 2px solid var(--primary-500);
  border-radius: var(--radius-full);
  animation: spin 1s linear infinite;
`;

export const LoadingDots = styled.div`
  display: flex;
  gap: var(--space-1);
  
  &::before,
  &::after,
  & {
    content: '';
    width: 8px;
    height: 8px;
    background: var(--primary-500);
    border-radius: var(--radius-full);
    animation: pulse 1.5s ease-in-out infinite;
  }
  
  &::before {
    animation-delay: -0.3s;
  }
  
  &::after {
    animation-delay: 0.3s;
  }
  
  /* Mobile-first utility classes */
  .mobile-hidden {
    @media (max-width: 768px) {
      display: none !important;
    }
  }
  
  .mobile-only {
    @media (min-width: 769px) {
      display: none !important;
    }
  }
  
  .desktop-hidden {
    @media (min-width: 769px) {
      display: none !important;
    }
  }
  
  .tablet-hidden {
    @media (min-width: 768px) and (max-width: 1024px) {
      display: none !important;
    }
  }
  
  /* Text alignment utilities for mobile */
  .text-center-mobile {
    @media (max-width: 768px) {
      text-align: center !important;
    }
  }
  
  .text-left-mobile {
    @media (max-width: 768px) {
      text-align: left !important;
    }
  }
  
  /* Spacing utilities for mobile */
  .mt-mobile-4 {
    @media (max-width: 768px) {
      margin-top: var(--space-4) !important;
    }
  }
  
  .mb-mobile-4 {
    @media (max-width: 768px) {
      margin-bottom: var(--space-4) !important;
    }
  }
  
  .p-mobile-4 {
    @media (max-width: 768px) {
      padding: var(--space-4) !important;
    }
  }
  
  /* Full width on mobile */
  .full-width-mobile {
    @media (max-width: 768px) {
      width: 100% !important;
    }
  }
`;
